import React from "react";
import LegalPage, { LegalSection } from "@/components/templates/LegalPage";
import { COMPANY, addressInline } from "@/data/company";

const EFFECTIVE = "February 2026";

const TermsAndConditions = () => (
  <LegalPage
    testid="terms-and-conditions-page"
    title="Website Terms & Conditions"
    description="Terms and conditions of use for the Lutrom Engineering Ltd website. Nothing on this page forms a contract for engineering services."
    path="/terms-and-conditions"
    h1="Website Terms & Conditions"
    effective={EFFECTIVE}
  >
    <p>
      These terms govern your use of the {COMPANY.name} website
      (<a href="https://www.lutrom.co.uk">www.lutrom.co.uk</a>). They should be read alongside
      our <a href="/privacy-policy">Privacy Policy</a> and{" "}
      <a href="/cookie-policy">Cookie Policy</a>. By using the website you agree to these terms.
    </p>

    <LegalSection title="Who we are">
      <p>
        The website is operated by {COMPANY.name} ({COMPANY.registered} · Company No.{" "}
        {COMPANY.companyNumber}), registered office and correspondence address:{" "}
        <span className="text-white">{addressInline()}</span>.
      </p>
      <p>
        Contact: <a href={COMPANY.email.mailto}>{COMPANY.email.display}</a> ·{" "}
        <a href={COMPANY.phone.tel}>{COMPANY.phone.display}</a>.
      </p>
    </LegalSection>

    <LegalSection title="Nature of the website">
      <p>
        The website is provided for general information about Lutrom Engineering Ltd and the
        services we can help with. It is not, and should not be treated as, a formal offer of
        service or a quotation. All engineering services are subject to a separate written
        quotation and scope agreed with you before work commences. Nothing on this website
        constitutes engineering, legal, regulatory or professional advice for your specific
        site or circumstances.
      </p>
    </LegalSection>

    <LegalSection title="Content accuracy">
      <p>
        We take reasonable care to keep the content on this website up-to-date and accurate,
        but we do not make any representation or warranty (express or implied) that the content
        is complete, current, error-free, or fit for a particular purpose. You should confirm
        details with us in writing before relying on any information on the website for a
        specific project, site or purchase decision.
      </p>
    </LegalSection>

    <LegalSection title="Intellectual property">
      <p>
        All content on the website — including text, layout, design, logos, photographs and
        graphics — is owned by or licensed to {COMPANY.name} and is protected by copyright and
        other intellectual-property rights. You may view, download and print pages from the
        website for personal or internal business use, provided that you do not modify the
        content and any copyright or other proprietary notices remain intact. You may not
        otherwise copy, reproduce, distribute, republish or exploit the content of the website
        without our prior written consent.
      </p>
    </LegalSection>

    <LegalSection title="Links to and from the website">
      <p>
        The website may contain links to third-party websites — for example our Client Portal
        or third-party providers referenced in our policies. Those websites are not under our
        control; we are not responsible for their content, privacy practices or availability.
        Following such links is at your own risk.
      </p>
      <p>
        You may link to our homepage in a way that is fair and legal and does not damage our
        reputation or take advantage of it. You may not frame the website, or otherwise
        misrepresent your relationship with us. We reserve the right to withdraw permission to
        link at any time.
      </p>
    </LegalSection>

    <LegalSection title="Acceptable use">
      <p>You agree not to use the website:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>In any way that breaches applicable law or regulation.</li>
        <li>
          To transmit any content that is unlawful, harmful, defamatory, obscene or otherwise
          objectionable.
        </li>
        <li>
          To attempt to gain unauthorised access to the website, the server on which it is
          hosted, or any connected system.
        </li>
        <li>
          To knowingly introduce viruses, trojans, worms or other technically harmful material.
        </li>
        <li>
          To scrape, harvest or systematically extract data from the website for any commercial
          purpose without our prior written consent.
        </li>
      </ul>
    </LegalSection>

    <LegalSection title="Limitation of liability">
      <p>
        Nothing in these terms excludes or limits our liability for death or personal injury
        caused by our negligence, for fraud or fraudulent misrepresentation, or for any other
        liability that cannot lawfully be excluded or limited under English law.
      </p>
      <p>
        Subject to that, to the fullest extent permitted by law, {COMPANY.name} and its
        directors, employees and agents will not be liable for any indirect, special or
        consequential loss (including loss of profits, business, contracts, revenue, goodwill
        or anticipated savings) arising out of or in connection with your use of, or inability
        to use, this website or reliance on any content contained in it.
      </p>
      <p>
        Nothing on the website constitutes a contract for the supply of engineering services.
        Where you engage us to carry out work, our contractual liability is governed by the
        separate written scope and terms of that engagement, not by these website terms.
      </p>
    </LegalSection>

    <LegalSection title="Changes to the website and these terms">
      <p>
        We may update, change or withdraw content from the website at any time without notice.
        We may also revise these terms from time to time — the &ldquo;Last updated&rdquo; date
        at the top of this page indicates when the terms were last revised. Continued use of
        the website after any change constitutes acceptance of the revised terms.
      </p>
    </LegalSection>

    <LegalSection title="Governing law and jurisdiction">
      <p>
        These terms and any dispute or claim arising out of or in connection with them
        (including any non-contractual dispute or claim) are governed by the laws of England
        and Wales. The courts of England and Wales have exclusive jurisdiction to settle any
        such dispute or claim.
      </p>
    </LegalSection>

    <LegalSection title="Contact">
      <p>
        For any question about these terms please contact:
      </p>
      <p className="text-white">
        {COMPANY.name}
        <br />
        {addressInline()}
        <br />
        <a href={COMPANY.email.mailto}>{COMPANY.email.display}</a>
        <br />
        <a href={COMPANY.phone.tel}>{COMPANY.phone.display}</a>
      </p>
    </LegalSection>
  </LegalPage>
);

export default TermsAndConditions;
